import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { theme } from '../theme';
import {
  trackScrollReading,
  getActiveScrollTodayReadings,
  getAllScrollProgress,
  ensureScrollProgressStarted,
  getSuggestedActiveScroll,
  isNewUser,
} from '../utils/progressTracker';
import BadgeEarnedPopup from './BadgeEarnedPopup';
import logo from '../assets/logo.png';

// Animated 30/30 completion badge SVG
const CompletionBadge = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="badgeGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="badgeFill" cx="30%" cy="30%" r="70%">
        <stop offset="0%" stopColor="#1e3a3a" />
        <stop offset="100%" stopColor="#0a1a2a" />
      </radialGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    {/* Outer glow ring */}
    <circle cx="32" cy="32" r="30" fill="url(#badgeGlow)" />
    {/* Wax seal outer ring */}
    <polygon
      points="32,2 37,12 48,8 44,19 55,22 48,30 55,38 44,41 48,52 37,48 32,58 27,48 16,52 20,41 9,38 16,30 9,22 20,19 16,8 27,12"
      fill="url(#badgeFill)"
      stroke="#14b8a6"
      strokeWidth="1.2"
      filter="url(#glow)"
    />
    {/* Inner circle */}
    <circle cx="32" cy="30" r="14" fill="none" stroke="#14b8a6" strokeWidth="0.8" opacity="0.6" />
    {/* Checkmark */}
    <path
      d="M24 30 L29 35 L40 24"
      fill="none"
      stroke="#14b8a6"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      filter="url(#glow)"
    />
    {/* 30/30 text */}
    <text
      x="32"
      y="46"
      textAnchor="middle"
      fill="#14b8a6"
      fontSize="6"
      fontWeight="700"
      fontFamily="system-ui, sans-serif"
      letterSpacing="0.5"
    >
      30/30
    </text>
  </svg>
);

function Dashboard() {
  const navigate = useNavigate();
  const [activeScrollReadings, setActiveScrollReadings] = useState({ readingsComplete: 0, completedReadings: [] });
  const [scrolls, setScrolls] = useState([]);
  const [scrollProgressMap, setScrollProgressMap] = useState({});
  const [activeScrollId, setActiveScrollId] = useState(null);
  const [activeScrollDbId, setActiveScrollDbId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [isTrialUser, setIsTrialUser] = useState(true);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [earnedBadge, setEarnedBadge] = useState(null);

  const userId = currentUser?.id;

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (userId) {
      loadAll();
      loadSubscriptionStatus();
      loadFirstName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (activeScrollId) {
      loadActiveScrollReadings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScrollId]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate('/'); return; }
    setCurrentUser(user);
  };

  const loadFirstName = async () => {
    if (!userId) return;
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('first_name')
      .eq('id', userId)
      .single();
    if (profile?.first_name) {
      setFirstName(profile.first_name);
    } else {
      const metadata = currentUser?.user_metadata || {};
      setFirstName(metadata.first_name || metadata.name || currentUser?.email?.split('@')[0] || 'Friend');
    }
  };

  const loadAll = async () => {
    await Promise.all([
      loadScrolls(),
      loadProgressAndActive(),
      checkOnboarding(),
    ]);
  };

  const checkOnboarding = async () => {
    const newUser = await isNewUser();
    setShowOnboarding(newUser);
  };

  const loadSubscriptionStatus = async () => {
    if (!userId) return;
    try {
      const { data } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (data) {
        if (data.subscription_status === 'active') {
          setIsTrialUser(false);
          setTrialDaysRemaining(0);
        } else if (data.trial_start_date) {
          const trialEndsAt = new Date(data.trial_start_date);
          trialEndsAt.setDate(trialEndsAt.getDate() + 30);
          const daysLeft = Math.ceil(
            (trialEndsAt - new Date()) / (1000 * 60 * 60 * 24)
          );
          setTrialDaysRemaining(Math.max(0, daysLeft));
          setIsTrialUser(true);
        }
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  };

  const loadScrolls = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('scrolls')
        .select('*')
        .eq('is_published', true)
        .order('scroll_sequence', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true });

      if (error) throw error;

      const mapped = data.map(scroll => ({
        id: scroll.identifier,
        dbId: scroll.id,
        title: scroll.title,
        subtitle: scroll.subtitle,
        scrollSequence: scroll.scroll_sequence,
        isOriginalTen: scroll.is_original_ten,
        trialAccess: scroll.identifier === 'habit',
      }));

      setScrolls(mapped);
    } catch (error) {
      console.error('Error loading scrolls:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProgressAndActive = async () => {
    try {
      const progressMap = await getAllScrollProgress();
      setScrollProgressMap(progressMap);

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('current_scroll_id')
        .eq('id', userId)
        .single();

      let resolvedActiveId = null;
      let resolvedActiveDbId = null;

      if (profileData?.current_scroll_id) {
        const { data: scrollData } = await supabase
          .from('scrolls')
          .select('identifier, id')
          .eq('id', profileData.current_scroll_id)
          .single();

        if (scrollData) {
          // Verify it's not completed
          const progress = progressMap[scrollData.identifier];
          if (!progress?.isCompleted) {
            resolvedActiveId = scrollData.identifier;
            resolvedActiveDbId = scrollData.id;
          }
        }
      }

      if (!resolvedActiveId) {
        resolvedActiveId = await getSuggestedActiveScroll();
        const { data: scrollData } = await supabase
          .from('scrolls')
          .select('id')
          .eq('identifier', resolvedActiveId)
          .single();
        if (scrollData) {
          resolvedActiveDbId = scrollData.id;
          await supabase
            .from('user_profiles')
            .update({ current_scroll_id: resolvedActiveDbId })
            .eq('id', userId);
        }
      }

      setActiveScrollId(resolvedActiveId);
      setActiveScrollDbId(resolvedActiveDbId);
    } catch (error) {
      console.error('Error loading progress/active scroll:', error);
    }
  };

  const loadActiveScrollReadings = async () => {
    const readings = await getActiveScrollTodayReadings(activeScrollId);
    setActiveScrollReadings(readings);
  };

  const handleDashboardReading = async (readingNumber) => {
    if (!activeScrollId) return;

    const isChecked = activeScrollReadings.completedReadings?.includes(readingNumber);

    if (isChecked) {
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
      await supabase
        .from('scroll_readings')
        .delete()
        .eq('user_id', userId)
        .eq('scroll_id', activeScrollId)
        .eq('reading_number', readingNumber)
        .eq('date', dateStr);
      await loadActiveScrollReadings();
      const updated = await getAllScrollProgress();
      setScrollProgressMap(updated);
      return;
    }

    const result = await trackScrollReading(activeScrollId, readingNumber);
    if (result?.success) {
      await loadActiveScrollReadings();
      const updated = await getAllScrollProgress();
      setScrollProgressMap(updated);
      if (showOnboarding) setShowOnboarding(false);
      // Show badge popup if any new badges earned
      if (result.newBadges?.length > 0) {
        setEarnedBadge(result.newBadges[0]);
      }
    }
  };

  const handleScrollSelect = async (scroll) => {
    if (isTrialUser && !scroll.trialAccess) {
      navigate('/payment');
      return;
    }
    await ensureScrollProgressStarted(scroll.id, scroll.dbId);
    navigate(`/scroll/${scroll.id}`);
  };

  const getQualifyingDays = (scrollId) => scrollProgressMap[scrollId]?.qualifyingDays || 0;
  const getDisplayQualifyingDays = (scrollId) => scrollProgressMap[scrollId]?.displayQualifyingDays ?? 1;
  const isScrollCompleted = (scrollId) => scrollProgressMap[scrollId]?.isCompleted || false;

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', paddingTop: '100px' }}>
          <p style={{ color: theme.colors.textPrimary, fontSize: '1.125rem' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const activeScroll = scrolls.find(s => s.id === activeScrollId);
  const activeDays = getQualifyingDays(activeScrollId);
  const activeDisplayDays = getDisplayQualifyingDays(activeScrollId);
  const activeIsCompleted = isScrollCompleted(activeScrollId);

  const libraryScrolls = scrolls.filter(
    s => s.id !== activeScrollId && !isScrollCompleted(s.id)
  );
  const completedScrolls = scrolls.filter(s => isScrollCompleted(s.id));

  return (
    <div style={styles.container}>

      {/* Badge earned popup */}
      {earnedBadge && (
        <BadgeEarnedPopup
          badge={earnedBadge}
          onDismiss={() => setEarnedBadge(null)}
        />
      )}

      {/* ── HEADER ── */}
<div style={styles.header}>
  <div style={styles.headerTop}>
    <div>
      <h1 style={styles.title}>My Greatest Self</h1>
      <p style={styles.greeting}>Welcome back, {firstName}</p>
    </div>
    <img
      src={logo}
      alt="My Greatest Self"
      style={styles.headerLogo}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  </div>
  <div style={styles.headerButtons}>
    <button style={styles.guideButton} onClick={() => navigate('/journey')}>
      📖 Guide
    </button>
    <button style={styles.progressButton} onClick={() => navigate('/progress')}>
      📊 Progress
    </button>
    <button style={styles.settingsButton} onClick={() => navigate('/settings')}>
      Settings
    </button>
  </div>
</div>
     
      {/* ── ONBOARDING BANNER ── */}
      {showOnboarding && (
        <div style={styles.onboardingBanner}>
          <div style={styles.onboardingIcon}>📜</div>
          <div style={styles.onboardingText}>
            <strong style={{ color: theme.colors.textPrimary, fontSize: '1.125rem' }}>
              Welcome to My Greatest Self — begin with the Scroll of Habit
            </strong>
            <p style={{ color: theme.colors.textSecondary, margin: '6px 0 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
              Every transformation begins here. Read it three times today — morning, midday, and evening.
              After 30 qualifying days, your journey continues automatically.
            </p>
          </div>
          <div style={styles.onboardingActions}>
            <button
              style={styles.onboardingStartButton}
              onClick={() => handleScrollSelect(scrolls.find(s => s.id === 'habit'))}
            >
              Begin Now →
            </button>
            <button
              style={styles.onboardingGuideButton}
              onClick={() => navigate('/journey')}
            >
              Read the Guide
            </button>
          </div>
        </div>
      )}

      {/* ── TRIAL BANNER ── */}
      {isTrialUser && !showOnboarding && (
        <div style={styles.trialBanner}>
          <div>
            <strong style={{ color: theme.colors.textPrimary }}>Free Trial</strong>
            <span style={{ color: theme.colors.textSecondary, fontSize: '0.875rem' }}>
              {' '}— {trialDaysRemaining} days remaining
            </span>
            <p style={styles.trialText}>
              You have access to the Scroll of Habit. Unlock all scrolls by subscribing.
            </p>
          </div>
          <button style={styles.upgradeButton} onClick={() => navigate('/payment')}>
            Unlock All Scrolls
          </button>
        </div>
      )}

      <div style={styles.content}>

        {/* ── ACTIVE SCROLL ── */}
        {activeScroll && (
          <div style={styles.activeSection}>
            <h2 style={styles.sectionLabel}>Your Active Scroll</h2>
            <div style={styles.activeCard}>
              <div style={styles.activeCardLeft}>
                <div style={styles.activeCardHeader}>
                  {activeScroll.scrollSequence && (
                    <span style={styles.activeScrollNumber}>#{activeScroll.scrollSequence}</span>
                  )}
                  <h3 style={styles.activeScrollTitle}>{activeScroll.title}</h3>
                  <span style={styles.activeScrollBadge}>📖 Active</span>
                </div>

                {activeScroll.subtitle && (
                  <p style={styles.activeScrollSubtitle}>{activeScroll.subtitle}</p>
                )}

                {/* Progress bar */}
                <div style={styles.progressBarWrap}>
                  <div style={styles.progressBarTrack}>
                    <div
                      style={{
                        ...styles.progressBarFill,
                        width: `${Math.min((activeDays / 30) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <span style={styles.progressBarLabel}>
                    {activeIsCompleted ? '30/30 Complete ✓' : `Day ${activeDisplayDays} of 30`}
                  </span>
                </div>

                <button
                  style={styles.continueButton}
                  onClick={() => handleScrollSelect(activeScroll)}
                >
                  {activeDays === 0 ? 'Begin Reading →' : 'Continue Reading →'}
                </button>
              </div>

              {/* Today's readings inline */}
              <div style={styles.activeCardRight}>
                <p style={styles.todayLabel}>Today's Readings</p>
                <div style={styles.checkboxList}>
                  {[
                    { num: 1, label: 'Morning',  emoji: '☀️' },
                    { num: 2, label: 'Midday',   emoji: '🌤️' },
                    { num: 3, label: 'Evening',  emoji: '🌙' },
                  ].map(({ num, label, emoji }) => {
                    const done = activeScrollReadings.completedReadings?.includes(num);
                    return (
                      <div key={num} style={styles.checkboxRow}>
                        <input
                          type="checkbox"
                          id={`dash-reading-${num}`}
                          checked={!!done}
                          onChange={() => handleDashboardReading(num)}
                          style={{ cursor: 'pointer', accentColor: theme.colors.teal }}
                        />
                        <label
                          htmlFor={`dash-reading-${num}`}
                          style={{
                            ...styles.checkboxLabel,
                            color: done ? theme.colors.teal : theme.colors.textSecondary,
                          }}
                        >
                          {emoji} {label} {done && '✓'}
                        </label>
                      </div>
                    );
                  })}
                </div>
                {activeScrollReadings.completedReadings?.length >= 2 &&
                  activeScrollReadings.completedReadings?.length < 3 && (
                  <p style={styles.qualifyNote}>✅ Qualifying day!</p>
                )}
                {activeScrollReadings.completedReadings?.length >= 3 && (
                  <p style={styles.qualifyNote}>🌟 Perfect day!</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── SCROLL LIBRARY ── */}
        {libraryScrolls.length > 0 && (
          <div style={styles.librarySection}>
            <h2 style={styles.sectionLabel}>Scroll Library</h2>
            <div style={styles.scrollGrid}>
              {libraryScrolls.map((scroll) => {
                const isLocked = isTrialUser && !scroll.trialAccess;
                return (
                  <div
                    key={scroll.id}
                    style={{
                      ...styles.libraryCard,
                      ...(isLocked ? styles.libraryCardLocked : {}),
                    }}
                    onClick={() => handleScrollSelect(scroll)}
                  >
                    <div style={styles.libraryCardHeader}>
                      {scroll.scrollSequence && (
                        <span style={styles.sequenceNumber}>#{scroll.scrollSequence}</span>
                      )}
                      <h4 style={styles.libraryCardTitle}>
                        {scroll.title.replace('The Scroll of ', '')}
                      </h4>
                      {isLocked && <span style={styles.lockIcon}>🔒</span>}
                    </div>
                    {scroll.subtitle && (
                      <p style={styles.libraryCardSubtitle}>{scroll.subtitle}</p>
                    )}
                    <button
                      style={isLocked ? styles.lockedButton : styles.selectButton}
                      onClick={(e) => { e.stopPropagation(); handleScrollSelect(scroll); }}
                    >
                      {isLocked ? '🔒 Unlock' : 'Read'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── COMPLETED SCROLLS ── */}
        {completedScrolls.length > 0 && (
          <div style={styles.completedSection}>
            <h2 style={styles.sectionLabel}>Completed</h2>
            <div style={styles.scrollGrid}>
              {completedScrolls.map((scroll) => (
                <div
                  key={scroll.id}
                  style={styles.completedCard}
                  onClick={() => handleScrollSelect(scroll)}
                >
                  {/* Badge top-right */}
                  <div style={styles.completedBadgeWrap}>
                    <CompletionBadge />
                  </div>

                  <div style={styles.completedCardHeader}>
                    {scroll.scrollSequence && (
                      <span style={styles.sequenceNumberCompleted}>#{scroll.scrollSequence}</span>
                    )}
                    <h4 style={styles.completedCardTitle}>
                      {scroll.title.replace('The Scroll of ', '')}
                    </h4>
                  </div>
                  {scroll.subtitle && (
                    <p style={styles.completedCardSubtitle}>{scroll.subtitle}</p>
                  )}
                  <button
                    style={styles.readAgainButton}
                    onClick={(e) => { e.stopPropagation(); handleScrollSelect(scroll); }}
                  >
                    Read Again
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: theme.colors.bgDark,
    padding: '20px',
    paddingBottom: '60px',
  },
  header: {
    maxWidth: '1200px',
    margin: '0 auto 24px',
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 0',
    gap: '6px',
  },
  title: {
    fontSize: '1.625rem',
    fontWeight: '700',
    background: theme.colors.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '4px',
  },
  greeting: {
    color: theme.colors.textSecondary,
    fontSize: '0.9375rem',
  },
  headerButtons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  guideButton: {
    background: 'linear-gradient(135deg, #6366f1 0%, #14b8a6 100%)',
    color: 'white',
    border: 'none',
    padding: '0.6923em 1.3846em',
    borderRadius: '8px',
    fontSize: '0.8125rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  progressButton: {
    background: 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)',
    color: theme.colors.bgDark,
    border: 'none',
    padding: '0.6923em 1.3846em',
    borderRadius: '8px',
    fontSize: '0.8125rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  settingsButton: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.blue}`,
    color: theme.colors.blue,
    padding: '0.6154em 1.2308em',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.8125rem',
  },

  onboardingBanner: {
    maxWidth: '1200px',
    margin: '0 auto 28px',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(20,184,166,0.15) 100%)',
    border: `2px solid ${theme.colors.blue}`,
    borderRadius: '16px',
    padding: '24px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap',
  },
  onboardingIcon: { fontSize: '2.5rem', flexShrink: 0 },
  onboardingText: { flex: 1, minWidth: '200px' },
  onboardingActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flexShrink: 0,
  },
  onboardingStartButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '10px',
    padding: '0.8em 1.6em',
    color: 'white',
    fontSize: '0.9375rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  onboardingGuideButton: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.blue}`,
    borderRadius: '10px',
    padding: '0.7143em 1.7143em',
    color: theme.colors.blue,
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  trialBanner: {
    maxWidth: '1200px',
    margin: '0 auto 28px',
    backgroundColor: theme.colors.bgMedium,
    border: `2px solid ${theme.colors.teal}`,
    borderRadius: '12px',
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  trialText: {
    color: theme.colors.textMuted,
    fontSize: '0.8125rem',
    margin: '4px 0 0 0',
  },
  upgradeButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.7857em 1.5714em',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
  },
  sectionLabel: {
    color: theme.colors.textSecondary,
    fontSize: '0.6875rem',
    fontWeight: '700',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    marginBottom: '14px',
  },

  // ── Active scroll card ──
  activeSection: {},
  activeCard: {
    backgroundColor: theme.colors.bgMedium,
    border: `2px solid ${theme.colors.blue}`,
    borderRadius: '16px',
    padding: '28px',
    display: 'flex',
    gap: '32px',
    flexWrap: 'wrap',
    boxShadow: `0 0 24px ${theme.colors.blue}20`,
  },
  activeCardLeft: {
    flex: 2,
    minWidth: '260px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  activeCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  activeScrollNumber: {
    color: theme.colors.teal,
    fontSize: '0.8125rem',
    fontWeight: '700',
  },
  activeScrollTitle: {
    color: theme.colors.textPrimary,
    fontSize: '1.375rem',
    fontWeight: '700',
    margin: 0,
    flex: 1,
  },
  activeScrollBadge: {
    backgroundColor: `${theme.colors.blue}25`,
    border: `1px solid ${theme.colors.blue}60`,
    borderRadius: '20px',
    padding: '3px 12px',
    fontSize: '0.75rem',
    color: theme.colors.blue,
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  activeScrollSubtitle: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    lineHeight: '1.5',
    margin: 0,
  },
  progressBarWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  progressBarTrack: {
    height: '6px',
    backgroundColor: theme.colors.bgLight,
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    background: theme.colors.gradient,
    borderRadius: '3px',
    transition: 'width 0.4s ease',
  },
  progressBarLabel: {
    color: theme.colors.textMuted,
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  continueButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '10px',
    padding: '0.8em 1.6em',
    color: 'white',
    fontSize: '0.9375rem',
    fontWeight: '700',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    marginTop: '4px',
  },
  activeCardRight: {
    flex: 1,
    minWidth: '180px',
    backgroundColor: theme.colors.bgDark,
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  todayLabel: {
    color: theme.colors.textSecondary,
    fontSize: '0.75rem',
    fontWeight: '700',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    margin: 0,
  },
  checkboxList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  checkboxLabel: {
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  qualifyNote: {
    fontSize: '0.75rem',
    color: theme.colors.teal,
    fontWeight: '600',
    margin: 0,
    padding: '8px 10px',
    backgroundColor: `${theme.colors.teal}12`,
    borderRadius: '6px',
  },

  // ── Library cards ──
  librarySection: {},
  scrollGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 45%), 1fr))',
    gap: '12px',
  },
  libraryCard: {
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '10px',
    padding: '1em',
    cursor: 'pointer',
    border: `1px solid ${theme.colors.bgLight}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    transition: 'border-color 0.2s',
  },
  libraryCardLocked: {
    opacity: 0.55,
  },
  libraryCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '6px',
  },
  sequenceNumber: {
    color: theme.colors.teal,
    fontSize: '0.6875rem',
    fontWeight: '700',
    flexShrink: 0,
    marginTop: '2px',
  },
  libraryCardTitle: {
    color: theme.colors.textPrimary,
    fontSize: '0.875rem',
    fontWeight: '600',
    margin: 0,
    flex: 1,
    lineHeight: '1.3',
  },
  lockIcon: {
    fontSize: '0.75rem',
    flexShrink: 0,
  },
  libraryCardSubtitle: {
    color: theme.colors.textSecondary,
    fontSize: '0.6875rem',
    lineHeight: '1.4',
    margin: 0,
    flex: 1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  selectButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '6px',
    padding: '0.5833em 1em',
    color: 'white',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 'auto',
  },
  lockedButton: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.blue}`,
    borderRadius: '6px',
    padding: '0.5833em 1em',
    color: theme.colors.blue,
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 'auto',
  },

  // ── Completed cards ──
  completedSection: {},
  completedCard: {
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '10px',
    padding: '1em',
    cursor: 'pointer',
    border: `1px solid ${theme.colors.teal}40`,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    position: 'relative',
    opacity: 0.82,
  },
  completedBadgeWrap: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  completedCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '6px',
    paddingRight: '52px',
  },
  sequenceNumberCompleted: {
    color: theme.colors.teal,
    fontSize: '0.6875rem',
    fontWeight: '700',
    flexShrink: 0,
    marginTop: '2px',
  },
  completedCardTitle: {
    color: theme.colors.textPrimary,
    fontSize: '0.875rem',
    fontWeight: '600',
    margin: 0,
    flex: 1,
    lineHeight: '1.3',
  },
  completedCardSubtitle: {
    color: theme.colors.textSecondary,
    fontSize: '0.6875rem',
    lineHeight: '1.4',
    margin: 0,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    paddingRight: '52px',
  },
  readAgainButton: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.teal}60`,
    borderRadius: '6px',
    padding: '0.5833em 1em',
    color: theme.colors.teal,
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 'auto',
 },
headerTop: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: '4px',
  width: '100%',
},
headerLogo: {
  width: '110px',
  height: '110px',
  objectFit: 'contain',
  flexShrink: 0,
  marginLeft: 'auto',
  paddingLeft: '16px',
},
}

export default Dashboard;